import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import EmailAlert from '../EmailAlert/emailalert';
import './emailform.scss';
import TestButton from '../TestButton/testbutton';
import InputField from '../InputField/inputfield';
import ReCAPTCHA from 'react-google-recaptcha';

const EmailForm = () => {
  const form = useRef(null);
  const [message, setMessage] = useState({ text: '', type: null });
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false); // New state for reCAPTCHA readiness
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const checkRecaptchaReady = () => {
      if (window.grecaptcha) {
        setIsRecaptchaReady(true);
      } else {
        setTimeout(checkRecaptchaReady, 100); // Retry until reCAPTCHA is ready
      }
    };

    checkRecaptchaReady();
  }, []);

  const handleRecaptchaChange = (token) => {
    console.log("Generated Token:", token); // Log the token to the console
    if (token) {
      setIsRecaptchaVerified(true);
      verifyRecaptchaToken(token); // Send the token to the backend
    } else {
      console.log("reCAPTCHA not completed");
      setIsRecaptchaVerified(false);
    }
  };

  const verifyRecaptchaToken = async (token) => {
    try {
      const response = await fetch('/verify-recaptcha.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
  
      const data = await response.json();
      if (data.success) {
        console.log('reCAPTCHA verified successfully');
      } else {
        console.error('reCAPTCHA verification failed:', data.message);
      }
    } catch (error) {
      console.error('Error verifying reCAPTCHA:', error);
    }
  };

  const handleBlur = (id, value, required) => {
    const element = document.getElementById(id);
    const parent = element?.closest('.form-group');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (required && !value.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [id]: "This field is required",
      }));
      if (parent) parent.classList.add('error');
    } else if (id === "user_email" && !emailRegex.test(value.trim())) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [id]: "Please enter a valid email address",
      }));
      if (parent) parent.classList.add('error');
    } else {
      setErrors((prevErrors) => {
        const { [id]: _, ...rest } = prevErrors;
        return rest;
      });
      if (parent) parent.classList.remove('error');
    }
  };

  const handleChange = (id, value, required) => {
    if (required && value.trim()) {
      setErrors((prevErrors) => {
        const { [id]: _, ...rest } = prevErrors;
        return rest;
      });

      const element = document.getElementById(id);
      const parent = element?.closest('.form-group');
      if (parent) parent.classList.remove('error');
    }
  };

  const alertRef = useRef(null);

  useEffect(() => {
    if (message.text && alertRef.current) {
      const offset = 60;
      const elementPosition = alertRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, [message]);

  useEffect(() => {
    window.recaptchaCallback = () => {
      setIsRecaptchaVerified(true);
    };

    window.recaptchaExpiredCallback = () => {
      setIsRecaptchaVerified(false);
    };
  }, []);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: null });
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  const validateForm = () => {
    if (!form.current) return false;

    let isValid = true;
    const formElements = form.current.elements;
    const newErrors = {};
    let firstErrorElement = null;

    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i];
      if (!element.name) continue;

      const parent = element.closest('.form-group');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (element.required && !element.value.trim()) {
        isValid = false;
        newErrors[element.id] = "This field is required";
        if (parent) parent.classList.add('error');
        if (!firstErrorElement) {
          firstErrorElement = element;
        }
      } else if (element.id === "user_email" && !emailRegex.test(element.value.trim())) {
        isValid = false;
        newErrors[element.id] = "Please enter a valid email address";
        if (parent) parent.classList.add('error');
        if (!firstErrorElement) {
          firstErrorElement = element;
        }
      } else {
        if (parent) parent.classList.remove('error');
      }
    }

    setErrors(newErrors);
    if (firstErrorElement) {
      firstErrorElement.focus();
    }

    return isValid;
  };

  const sendEmail = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({ text: 'Please fix the errors in the form before submitting.', type: 'error' });
      return;
    }

    if (!isRecaptchaVerified) {
      setMessage({ text: 'Please verify the reCAPTCHA before submitting the form.', type: 'error' });
      return;
    }

    const YOUR_SERVICE_ID = 'service_ek3y8l7';
    const YOUR_TEMPLATE_ID = 'template_zveuqmd';
    const YOUR_PUBLIC_KEY = 'MF9H7bt37ElhMRFIU';

    if (form.current) {
      emailjs
        .sendForm(YOUR_SERVICE_ID, YOUR_TEMPLATE_ID, form.current, {
          publicKey: YOUR_PUBLIC_KEY,
        })
        .then(
          () => {
            setMessage({ text: 'Message sent successfully! We will get back to you shortly.', type: 'success' });
            form.current.reset();
          },
          (error) => {
            setMessage({ text: `Failed to send message: ${error.text}`, type: 'error' });
          }
        );
    } else {
      setMessage({ text: 'Form reference is null.', type: 'error' });
    }
  };

  return (
    <>
      {message.text && (
        <div ref={alertRef}>
          <EmailAlert
            variant={message.type === 'success' ? 'success' : 'error'}
            title={message.text}
            onClose={() => setMessage({ text: '', type: null })}
          />
        </div>
      )}
      <form method="POST" ref={form} onSubmit={sendEmail} noValidate>
        <InputField
          id="user_name"
          label="Name"
          name="user_name"
          required={true}
          placeholder="Enter your first and last name"
          errorMessage={errors["user_name"]}
          handleBlur={handleBlur}
          handleChange={handleChange}
        />
        <InputField
          id="user_email"
          label="Email"
          name="user_email"
          required={true}
          placeholder="Enter your email address"
          errorMessage={errors["user_email"]}
          handleBlur={handleBlur}
          handleChange={handleChange}
        />
        <InputField
          id="message"
          label="How can we help you?"
          name="message"
          required={true}
          placeholder="Please describe your business or project in detail"
          multiline={true}
          rows={4}
          errorMessage={errors["message"]}
          handleBlur={handleBlur}
          handleChange={handleChange}
        />
         {isRecaptchaReady ? (
          <ReCAPTCHA
            sitekey="6LdMFSArAAAAAKyiRn5a2DRfEO88SaKkneXSzNV5"
            onChange={handleRecaptchaChange}
            onExpired={() => setIsRecaptchaVerified(false)}
          />
        ) : (
          <p>Loading reCAPTCHA...</p>
        )}
        <TestButton
          className="submitForm"
          variant="primary"
          disabled={!isRecaptchaVerified}
          type="submit"
          value="Submit form"
        />
      </form>
    </>
  );
};

export default EmailForm;